import 'normalize.css/normalize.css';
import '../../styles/base.css';

import React, {Component} from 'react';
import {I18nProvider} from '@lingui/react';
import {Helmet} from 'react-helmet';
import {i18n, loadCatalog, getLanguageCode} from '../../services/i18n';
import {hot} from 'react-hot-loader/root';
import {bindActionCreators} from 'redux';
import {connect, Provider, useDispatch, useSelector} from 'react-redux';
import {Switch, Route, withRouter, useRouteMatch, useParams} from 'react-router-dom';
import {push} from 'connected-react-router/immutable';
import {ConnectedRouter} from 'connected-react-router/immutable';
import {NotificationStack} from 'react-notification';

import config from 'config';
import {local, logsBackup, permanentErrorsBackup, stateBackup} from '@services/local';
import {makeSyncAllRequest, makeSyncRequests, makeOutcomesRequests} from '@services/sync';
import {buildPrivateRoute, isAuthenticated} from '@services/auth';
import {QueuesProvider} from '@services/context';

import {getUserFullName, getUserId} from '@reducers/user';
import {isAccountWidgetOpen} from '@reducers/ui';
import {getNotifications} from '@reducers/notifications';
import {logout, unauthorized, refreshLogin} from '@actions/login';
import {openAccountWidget, closeAccountWidget, setIsMobile} from '@actions/ui';
import {isCalendarTheme} from '@reducers/ui';
import {isFerrariTheme} from '@reducers/ui';
import {checkFirstPageLoad} from '@actions/fluxes';
import {dismissNotification} from '@actions/notifications';
import {getTutorialState} from '@reducers/testroom';
import {getUserCampaign, getUserCampaignList} from '@reducers/userCampaign';
import {getOldStateBackupKeys} from '@services/utilities';
import {RequestsQueue} from '@services/requestsQueue';
import {LogsQueue} from '@services/logsQueue';

import Page from '@components/Page';
import MainHeader from '../MainHeader';
import Login from '../Login';
import ResetPassword from '../ResetPassword';
import ErrorPage from '@containers/ErrorPage';
import NewPassword from '../NewPassword';
import Privacy from '../Privacy';
import TestRoom from '../TestRoom';
import Home from '@components/Home';
import Dashboard from '@components/Dashboard';
import CalendarTheme from '@containers/CalendarTheme';
import FerrariTheme from '@containers/FerrariTheme';
import LoadingSpinner from '@components/LoadingSpinner';
import SSOLogoutPage from '@containers/SSOLogoutPage';
import TestContainer from '../TestContainer';
import TestDetails from '@components/TestDetails';
import StartTest from '../StartTest';
import Reports from '../ReportsPage';
// import LangSwitcher from '@components/LangSwitcher/LangSwitcher';
import CampaignSelector from '@components/CampaignSelector';
import Dashboard360 from '../Skillup360/Dashboard360';
import Questionnaire360Container from '../Skillup360/Questionnaire360Container';

import {
  HOME,
  LOGIN,
  RESET,
  NEW_PASSWORD,
  PRIVACY,
  TEST,
  TEST_START,
  REPORT,
  TESTS,
  CAMPAIGNSELECTOR,
  QUESTIONNAIRE360,
  EVALUATION360,
  SSO_ERROR,
  SSO_LOGIN_SHORTCUT,
  SSO_LOGOUT,
} from '../../services/routes';

//import cl from 'classnames';
import s from './App.css';

const NotFound = () => (
  <div>
    <p>Not found...</p>
  </div>
);

const AppPage = props => <Page header={MainHeader} {...props} />;
const LoginPage = props => <AppPage component={Login} customHeader {...props} />;
const ResetPasswordPage = props => <AppPage component={ResetPassword} customHeader {...props} />;
const NewPasswordPage = props => <AppPage component={NewPassword} customHeader {...props} />;
const PrivacyPage = props => <AppPage component={Privacy} customHeader {...props} />;
const tutorialCompleted = local.get('tutorialCompleted');
const HomePage = props => <AppPage component={TestRoom} leftComponent={Home} {...props} />;
const DashboardPage = props => (
  <AppPage component={TestRoom} leftComponent={Dashboard} {...props} />
);
const NotFoundPage = props => <AppPage component={NotFound} {...props} />;
const TestContainerPage = props => <AppPage component={TestContainer} hideMenu={true} {...props} />;
const StartPage = props => <AppPage component={StartTest} leftComponent={TestDetails} {...props} />;
const ReportsPage = props => <AppPage component={Reports} {...props} />;
const CampaignSelectorPage = props => (
  <AppPage component={CampaignSelector} customHeader {...props} />
);
const CalendarThemePage = props => <AppPage component={CalendarTheme} hideMenu={true} {...props} />;
const FerrariThemePage = props => <FerrariTheme {...props} />;
const Questionnaire360Dashboard = props => (
  <AppPage component={Dashboard360} hideMenu={true} {...props} />
);
const Questionnaire360ContainerPage = props => (
  <AppPage component={Questionnaire360Container} hideMenu={true} {...props} />
);

const MainHeaderWithRouter = withRouter(MainHeader);

const locale = navigator.language || navigator.userLanguage;
const lang = getLanguageCode(locale);
loadCatalog(lang);

class App extends Component {
  constructor(props) {
    super(props);

    this.state = {showCompleteTestModal: false};

    this.PrivateRoute = buildPrivateRoute(props.store);
    this.logsQueue = new LogsQueue(logsBackup.get('items'), {
      onUpdate: items => logsBackup.set('items', items),
      onUnloggableError: err => permanentErrorsBackup.append(err),
    });
    // process the local logs backup
    this.logsQueue.process();

    this.requestsQueue = new RequestsQueue({
      onAuthError: () => props.unauthorized(),
      onLogError: data => this.logsQueue.push(data),
      onSyncAll: this.onSyncAll,
    });
    this.requestsQueueInitialized = false;
  }

  componentDidMount() {
    // This needs to be dispatched when the connected router has already been mounted
    this.props.checkFirstPageLoad();

    window.addEventListener('unload', this.exhaustQueues);
    window.addEventListener('resize', this.checkScreenSize);
  }

  checkScreenSize = () => {
    const isMobile = window.matchMedia('(min-width: 320px) and (max-width: 480px)').matches;

    if (isMobile) {
      //dispatch redux action to change ui state
      this.props.setIsMobile(true);
      return;
    }

    this.props.setIsMobile(false);
  };

  exhaustQueues = () => {
    this.requestsQueue.exhaust();
    this.logsQueue.exhaust();
  };

  componentWillReceiveProps(props) {
    const {campaignsList} = props;
    const campaignId = props.userCampaign.get('id');

    if (
      !this.requestsQueueInitialized &&
      props.isLoggedIn &&
      props.userId &&
      campaignId &&
      campaignsList.size > 0
    ) {
      const otherUsers = stateBackup.keys().filter(u => u.toString() !== props.userId.toString());
      otherUsers.forEach(u => {
        this.logsQueue.push({userId: u, state: stateBackup.get(u)});
        stateBackup.remove(u);
      });

      const currentUserState = stateBackup.get(props.userId);
      if (currentUserState) {
        const oldStateKeys = getOldStateBackupKeys(currentUserState);

        if (oldStateKeys.length > 0) {
          //I'M NEVER SURE WHAT CAMPAIGN
          //THIS TRACKING DATA BELONGS TO
          //THERE MAY BE AN OLD ONE WHICH IS TURNED OFF
          for (let key of oldStateKeys) {
            const pendingData = currentUserState[key];
            //send it to logs
            this.logsQueue.push({
              message: 'not found campaignId in backup state',
              userId: props.userId,
              testId: key,
              state: pendingData,
            });
            delete currentUserState[key];
          }

          const newBackupState = {[campaignId]: currentUserState};
          stateBackup.set(props.userId, newBackupState);
        }

        const newCurrentUserState = stateBackup.get(props.userId);
        const allCampaignsWithPendingData = Object.keys(newCurrentUserState);

        for (let pendingCampaignId of allCampaignsWithPendingData) {
          const state = newCurrentUserState[pendingCampaignId];
          if (state) {
            this.requestsQueue.push(
              makeSyncAllRequest({
                campaignId: pendingCampaignId,
                userId: props.userId,
                state,
              })
            );
          }
        }
      }

      this.requestsQueueInitialized = true;
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.userCampaign.getIn(['id']) !== this.props.userCampaign.getIn(['id'])) {
      this.loadLocale();
    }

    const prevCompletedTests = prevProps.completedTests;
    const {completedTests} = this.props;

    if (prevCompletedTests?.size && completedTests?.size) {
      if (prevCompletedTests.size !== completedTests.size) {
        this.setState({showCompleteTestModal: true});
      }
    }
  }

  closeCompleteTestModal = () => {
    this.setState({showCompleteTestModal: false});
  };

  loadLocale = () => {
    const {userCampaign} = this.props;

    if (userCampaign && userCampaign.get('lang')) {
      //if it's already it return before reactivating
      loadCatalog(userCampaign.getIn(['user', 'lang']).split('-').join('_').toLowerCase()).then(
        _ => {
          console.log('locale changed');
          this.checkScreenSize();
        }
      );
      return;
    }
  };

  onSyncAll = (task, needPushing, campaignId) => {
    const {userId} = this.props;
    if (task.userId !== userId) {
      return;
    }

    const currentUserState = stateBackup.get(userId);
    if (currentUserState && campaignId) {
      const syncRequests = makeSyncRequests({
        campaignId,
        userId,
        state: currentUserState,
        needPushing,
      });
      syncRequests.forEach(req => this.requestsQueue.unshift(req));

      const outcomesRequests = makeOutcomesRequests({
        campaignId,
        userId,
        state: currentUserState,
      });
      outcomesRequests.forEach(req => this.requestsQueue.unshift(req));
    }
  };

  // Change notifications animation: from top and using the more performant translate property
  getBarStyle = (index, style) => {
    return Object.assign({}, style, {
      top: '2rem',
      left: '50%',
      bottom: 'auto',
      transform: 'translate3d(-50%, -200%, 0)',
      WebkitTransform: 'translate3d(-50%, -200%, 0)',
      zIndex: 10000,
    });
  };

  getActiveBarStyle = (index, style) => {
    return Object.assign({}, style, {
      transform: `translate3d(-50%, ${2 + index * 4}rem, 0)`,
      WebkitTransform: `translate3d(-50%, ${2 + index * 4}rem, 0)`,
    });
  };

  onDismissNotification = notification => this.props.dismissNotification(notification);

  render() {
    const {history, store, notifications, awOpen, closeAccountWidget, tutorialCompleted} =
      this.props;
    const {showCompleteTestModal} = this.state;
    const PrivateRoute = this.PrivateRoute;

    return (
      <Provider store={store}>
        <QueuesProvider logsQueue={this.logsQueue} requestsQueue={this.requestsQueue}>          <ConnectedRouter history={history}>
            <I18nProvider i18n={i18n}>
              <Helmet>
                <title>
                  {i18n._('app.page_title', null, {
                    defaults: 'Skillup',
                  })}
                </title>
              </Helmet>
              <div
                className={s.AppContainer}
                id="AppContainer"
                onClick={awOpen ? closeAccountWidget : null}
              >
                <header id="main-header" />

                <Switch>
                  <Route path={LOGIN} component={LoginPage} />
                  <Route path={RESET} component={ResetPasswordPage} />
                  <Route path={NEW_PASSWORD} component={NewPasswordPage} />
                  <Route path={PRIVACY} component={PrivacyPage} />
                  <Route path={SSO_ERROR} component={ErrorPage} />
                  <Route path={SSO_LOGOUT} component={SSOLogoutPage} />
                  <Route path={SSO_LOGIN_SHORTCUT} component={SSOShortcut} />

                  <PrivateRoute path={CAMPAIGNSELECTOR} component={CampaignSelectorPage} />

                  <Route>
                    <AppMain
                      closeCompleteTestModal={this.closeCompleteTestModal}
                      showCompleteTestModal={showCompleteTestModal}
                      PrivateRoute={PrivateRoute}
                    />
                  </Route>
                </Switch>

                <footer id="main-footer" />

                <NotificationStack
                  notifications={notifications.take(config.maxNotificationsNum || 4).toArray()}
                  onDismiss={this.onDismissNotification}
                  barStyleFactory={this.getBarStyle}
                  activeBarStyleFactory={this.getActiveBarStyle}
                />
              </div>
            </I18nProvider>
          </ConnectedRouter>
        </QueuesProvider>
      </Provider>
    );
  }
}

function AppMain({closeCompleteTestModal, showCompleteTestModal, PrivateRoute}) {
  const dispatch = useDispatch();
  const isCalendar = useSelector(state => isCalendarTheme(state));
  const isFerrari = useSelector(state => isFerrariTheme(state));
  const userCampaign = useSelector(state => state.getIn(['userCampaign', 'data']));
  const userCampaignList = useSelector(state => state.getIn(['userCampaign', 'list']));

  React.useEffect(() => {
    if (!userCampaign || userCampaign.size === 0) {
      dispatch(refreshLogin());
    }
  }, [userCampaign]);

  const isLoadingCampaignData = userCampaign.size === 0;
  const isLoading = isLoadingCampaignData || isCalendar === null;

  if (isLoading) {
    return (
      <main className={s.Main}>
        <div>
          <LoadingSpinner />
        </div>
      </main>
    );
  }

  return (
    <main className={s.Main}>
      <Switch>
        {/* IF THEMEID IS CALENDAR (2) HIDE THE TESTS, TEST, REPORT AND TEST_START ROUTES,
              AND INSTEAD OF DASHBOARDPAGE ADD CALENDARTHEME CONTAINER TO THE HOMEROUTE
              COMPONENT */}

        {isCalendar ? (
          <PrivateRoute
            exact
            path={HOME}
            component={CalendarThemePage}
            closeCompleteTestModal={closeCompleteTestModal}
            showCompleteTestModal={showCompleteTestModal}
          />
        ) : isFerrari ? (
          <PrivateRoute exact path={HOME} component={FerrariThemePage} />
        ) : !isLoadingCampaignData ? (
          <PrivateRoute
            exact
            path={HOME}
            component={tutorialCompleted || true ? DashboardPage : HomePage}
          />
        ) : null}
        <PrivateRoute
          exact
          path={TEST}
          component={isFerrari ? FerrariThemePage : TestContainerPage}
        />
        <PrivateRoute exact path={TESTS} component={DashboardPage} />
        <PrivateRoute exact path={REPORT} component={ReportsPage} />
        <PrivateRoute exact path={TEST_START} component={StartPage} />
        <PrivateRoute exact path={QUESTIONNAIRE360} component={Questionnaire360Dashboard} />
        <PrivateRoute exact path={EVALUATION360} component={Questionnaire360ContainerPage} />
        <Route component={NotFoundPage} />
        {/* [LOGIN, RESET, NEW_PASSWORD, PRIVACY].map((path, index) => (
                      <Route path={path} key={index} component={LangSwitcher} />
                    )) */}
      </Switch>
    </main>
  );
}

function SSOShortcut() {
  const {slug} = useParams();

  React.useEffect(() => {
    if (slug) {
      window.location.href = `/api/v2/auth/sso/${slug}`;
    }
  }, [slug]);

  return (
    <main className={s.Main}>
      <LoadingSpinner />
    </main>
  );
}

function mapStateToProps(state) {
  const isLoggedIn = isAuthenticated(state);
  const userFullName = getUserFullName(state);
  const userId = getUserId(state);
  const userCompanyId = state.getIn(['user', 'data', 'company', 'id']);
  const awOpen = isAccountWidgetOpen(state);
  const notifications = getNotifications(state);
  const path = state.getIn(['router', 'location', 'pathname']);
  const tutorialCompleted = getTutorialState(state);
  const userCampaign = getUserCampaign(state);
  const campaignsList = getUserCampaignList(state);
  const completedTests = state
    .getIn(['tests', 'list'])
    .filter(test => test.get('status') === 'complete');

  return {
    isLoggedIn,
    userFullName,
    userCompanyId,
    userId,
    userCampaign,
    awOpen,
    notifications,
    path,
    tutorialCompleted,
    completedTests,
    campaignsList,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      push,
      logout,
      unauthorized,
      checkFirstPageLoad,
      openAccountWidget,
      closeAccountWidget,
      dismissNotification,
      setIsMobile,
    },
    dispatch
  );
}

const ConnectedApp = connect(mapStateToProps, mapDispatchToProps, null, {pure: false})(App);

export default hot(ConnectedApp);
